import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { describeError } from '@pc/core';
import {
  Button,
  Field,
  Input,
  READ_ONLY_TOOLTIP,
  SmallCap,
  useCanWriteOrg,
} from '@pc/ui';
import type {
  FulfillmentOrderFulfillmentLine,
  FulfillmentOrderFulfillmentShipment,
  ShippingShipmentAddress,
} from '@pc/db';
import { useActiveCtx } from '../useCtx';
import {
  useCancelShipment,
  useCreateShipmentWithLines,
  useMarkOrderLinesPickedUp,
  useOrderFulfillmentState,
} from '../queries';
import { StatusChip } from './StatusChip';
import { ShipmentFulfillmentSection } from './ShipmentFulfillmentSection';

interface Props {
  orderId: string;
  orgId: string;
  storeId: string;
  shipToSnapshot: Record<string, unknown> | null;
}

/**
 * Per-order fulfillment surface rendered on the order detail page. Now
 * understands split shipments + partial pickup:
 *
 *   * Lists every order line with its allocation badges (shipped,
 *     picked-up, remaining).
 *   * Lists every shipment attached to the order, with the items it
 *     carries inline. Each shipment links to its packing station.
 *   * Lets the operator pick which remaining lines/qty go into the next
 *     shipment, OR mark them as picked-up at the counter.
 *   * Shows a "remaining to ship" banner whenever the order still has
 *     unallocated qty -- prevents leftovers from being forgotten.
 *
 * All mutations route through SECURITY DEFINER RPCs introduced in
 * supabase/migrations/20260627001100_fulfillment_split_shipments_and_line_allocations.sql.
 */
function shipToFromSnapshot(
  snap: Record<string, unknown> | null,
): ShippingShipmentAddress {
  const ship = snap ?? {};
  return {
    name: (ship.name as string) ?? '',
    street1: (ship.line1 as string) ?? '',
    street2: (ship.line2 as string) ?? undefined,
    city: (ship.city as string) ?? '',
    state: (ship.region as string) ?? '',
    zip: (ship.postal_code as string) ?? '',
    country: (ship.country as string) ?? 'US',
  };
}

export function FulfillmentShipmentPanel({ orderId, shipToSnapshot }: Props) {
  const ctx = useActiveCtx();
  const canWrite = useCanWriteOrg();
  const stateQ = useOrderFulfillmentState(ctx, orderId);
  const createShipment = useCreateShipmentWithLines(ctx);
  const markPickedUp = useMarkOrderLinesPickedUp(ctx);

  const state = stateQ.data;
  const lines = state?.lines ?? [];
  const shipments = state?.shipments ?? [];
  const pickups = state?.pickups ?? [];
  const totals = state?.totals;

  const remainingLines = useMemo(
    () => lines.filter((l) => Number(l.remaining_qty) > 0),
    [lines],
  );

  const [selection, setSelection] = useState<Record<string, number>>({});
  const [pickedUpBy, setPickedUpBy] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const lineLookup = useMemo(() => {
    const m = new Map<string, FulfillmentOrderFulfillmentLine>();
    lines.forEach((l) => m.set(l.order_line_id, l));
    return m;
  }, [lines]);

  const selectedTotal = useMemo(
    () =>
      Object.values(selection).reduce(
        (sum, q) => sum + (Number.isFinite(q) ? q : 0),
        0,
      ),
    [selection],
  );

  function setQty(orderLineId: string, qty: number, max: number) {
    const clamped = Math.max(0, Math.min(qty, max));
    setSelection((prev) => {
      const next = { ...prev };
      if (clamped === 0) {
        delete next[orderLineId];
      } else {
        next[orderLineId] = clamped;
      }
      return next;
    });
  }

  function selectAllRemaining() {
    const next: Record<string, number> = {};
    remainingLines.forEach((l) => {
      next[l.order_line_id] = Number(l.remaining_qty);
    });
    setSelection(next);
  }

  function clearSelection() {
    setSelection({});
  }

  function buildPayload(): { orderLineId: string; quantity: number }[] {
    return Object.entries(selection)
      .filter(([, qty]) => qty > 0)
      .map(([orderLineId, quantity]) => ({ orderLineId, quantity }));
  }

  async function onCreateShipment() {
    setErr(null);
    const payload = buildPayload();
    if (payload.length === 0) {
      setErr('Pick at least one line for this shipment.');
      return;
    }
    try {
      await createShipment.mutateAsync({ orderId, lines: payload });
      clearSelection();
    } catch (e) {
      setErr(describeError(e));
    }
  }

  async function onMarkPickedUp() {
    setErr(null);
    const payload = buildPayload();
    if (payload.length === 0) {
      setErr('Pick at least one line to mark picked up.');
      return;
    }
    try {
      await markPickedUp.mutateAsync({
        orderId,
        lines: payload,
        pickedUpBy: pickedUpBy.trim() || null,
      });
      clearSelection();
      setPickedUpBy('');
    } catch (e) {
      setErr(describeError(e));
    }
  }

  if (stateQ.isLoading) {
    return (
      <div className="border border-rule bg-paper-2 p-4 text-sm text-muted">
        Loading fulfillment state...
      </div>
    );
  }
  if (stateQ.error) {
    return (
      <div className="border border-clay bg-clay/10 p-4 text-sm text-clay">
        {describeError(stateQ.error)}
      </div>
    );
  }
  if (!state) {
    return null;
  }

  const remainingQty = Number(totals?.remaining_qty ?? 0);
  const hasRemaining = remainingQty > 0;
  const shipTo = shipToFromSnapshot(shipToSnapshot);

  return (
    <div className="flex flex-col gap-5">
      {/* Status banner — the headline state of the order's fulfillment.
          A left accent bar (not a full box) keeps it from competing with
          the section panels below. */}
      {hasRemaining ? (
        <div className="border-l-2 border-clay bg-clay/5 pl-3 pr-2 py-2 text-sm text-ink">
          <span className="display">
            {remainingQty.toLocaleString()} {remainingQty === 1 ? 'unit' : 'units'} still to ship
          </span>{' '}
          <span className="text-muted tabular">
            across {remainingLines.length} line{remainingLines.length === 1 ? '' : 's'}
          </span>
          <p className="text-xs text-muted mt-0.5">
            Assign the remaining items below, then ship them or mark them picked up.
          </p>
        </div>
      ) : (
        <div className="border-l-2 border-olive bg-olive/5 pl-3 pr-2 py-2 text-sm text-ink">
          Every item on this order is accounted for. Shipments below show how it was split.
        </div>
      )}

      {/* Per-line allocation table */}
      <div>
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <SmallCap>Order items</SmallCap>
          <div className="flex items-center gap-3 text-xs">
            {remainingLines.length > 0 ? (
              <>
                <button
                  type="button"
                  className="text-muted hover:text-ink underline"
                  onClick={selectAllRemaining}
                >
                  Select all remaining
                </button>
                {selectedTotal > 0 ? (
                  <button
                    type="button"
                    className="text-muted hover:text-ink underline"
                    onClick={clearSelection}
                  >
                    Clear
                  </button>
                ) : null}
              </>
            ) : null}
            <Link to="/fulfillment" className="text-muted hover:text-ink underline">
              Open queue
            </Link>
          </div>
        </div>
        <ul className="flex flex-col">
          {lines.map((l) => {
            const remaining = Number(l.remaining_qty);
            const ordered = Number(l.quantity);
            const shipped = Number(l.shipped_qty);
            const pickedUp = Number(l.picked_up_qty);
            const selected = selection[l.order_line_id] ?? 0;
            return (
              <li
                key={l.order_line_id}
                className="border-t border-rule first:border-t-0 py-2"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-sm text-ink truncate">
                    <span className="tabular text-xs text-muted mr-2">
                      #{(l.position ?? 0) + 1}
                    </span>
                    {l.description}
                  </div>
                  <div className="text-xs text-muted tabular whitespace-nowrap">
                    {ordered.toLocaleString()} ordered
                  </div>
                </div>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center">
                  <div className="flex flex-wrap items-center gap-1.5 text-xs">
                    {shipped > 0 ? (
                      <span className="border border-rule px-1.5 py-0.5">
                        Shipped {shipped.toLocaleString()}
                      </span>
                    ) : null}
                    {pickedUp > 0 ? (
                      <span className="border border-rule px-1.5 py-0.5">
                        Picked up {pickedUp.toLocaleString()}
                      </span>
                    ) : null}
                    {remaining > 0 ? (
                      <span className="border border-clay/40 bg-clay/10 px-1.5 py-0.5 text-ink">
                        {remaining.toLocaleString()} remaining
                      </span>
                    ) : (
                      <span className="border border-olive/40 bg-olive/10 px-1.5 py-0.5 text-ink">
                        Fully allocated
                      </span>
                    )}
                  </div>
                  {remaining > 0 ? (
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-muted">
                        Assign:
                      </label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={remaining}
                        value={selected || ''}
                        onChange={(e) =>
                          setQty(
                            l.order_line_id,
                            parseInt(e.target.value || '0', 10) || 0,
                            remaining,
                          )
                        }
                        placeholder="0"
                        className="bg-paper border border-rule-strong px-2 py-1 text-sm w-20 tabular"
                      />
                      <button
                        type="button"
                        className="text-xs text-muted hover:text-ink underline"
                        onClick={() =>
                          setQty(l.order_line_id, remaining, remaining)
                        }
                      >
                        max
                      </button>
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Primary action zone for the selected lines. One intentional
          bordered box so the next step stands out from the read-only
          lists above and below it. */}
      {remainingLines.length > 0 ? (
        <div className="border border-rule-strong bg-paper p-4 flex flex-col gap-3">
          <div className="flex items-baseline justify-between gap-3">
            <SmallCap>Fulfill selected items</SmallCap>
            <span className="text-xs text-muted tabular">
              {selectedTotal.toLocaleString()} selected
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <div className="flex flex-col gap-2">
              <p className="text-xs text-muted flex-1">
                Creates a shipment for the selected items. Buy a Shippo label or
                add 3rd-party tracking on the shipment row below.
              </p>
              <Button
                size="sm"
                disabled={!canWrite || createShipment.isPending || selectedTotal === 0}
                title={canWrite ? undefined : READ_ONLY_TOOLTIP}
                onClick={onCreateShipment}
              >
                {createShipment.isPending ? 'Creating shipment...' : 'Create shipment'}
              </Button>
            </div>
            <div className="flex flex-col gap-2 sm:border-l sm:border-rule sm:pl-6">
              <p className="text-xs text-muted flex-1">
                Skip the label and record that the customer collected these items at
                the counter.
              </p>
              <div className="flex items-end gap-2">
                <Field label="Picked up by (optional)">
                  <Input
                    value={pickedUpBy}
                    onChange={(e) => setPickedUpBy(e.target.value)}
                    placeholder="Customer name"
                  />
                </Field>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!canWrite || markPickedUp.isPending || selectedTotal === 0}
                  title={canWrite ? undefined : READ_ONLY_TOOLTIP}
                  onClick={onMarkPickedUp}
                >
                  {markPickedUp.isPending ? 'Recording...' : 'Mark picked up'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* History: shipments + counter pickups, grouped under one divider
          so they read as a record of what already happened. */}
      {shipments.length > 0 || pickups.length > 0 ? (
        <div className="flex flex-col gap-4 border-t border-rule pt-4">
          {shipments.length > 0 ? (
            <div>
              <SmallCap>
                Shipments ({shipments.filter((s) => !s.cancelled_at).length} active)
              </SmallCap>
              <ul className="flex flex-col mt-2">
                {shipments.map((s) => (
                  <li key={s.id} className="border-t border-rule first:border-t-0">
                    <ShipmentSummary
                      shipment={s}
                      lineLookup={lineLookup}
                      orderId={orderId}
                      shipTo={shipTo}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {pickups.length > 0 ? (
            <div>
              <SmallCap>Counter pickups</SmallCap>
              <ul className="mt-2 flex flex-col text-sm">
                {pickups.map((p) => {
                  const line = lineLookup.get(p.order_line_id);
                  return (
                    <li
                      key={p.id}
                      className="border-t border-rule first:border-t-0 py-2 flex items-baseline justify-between gap-3"
                    >
                      <div className="min-w-0 truncate">
                        <span className="tabular text-xs text-muted mr-2">
                          {Number(p.quantity).toLocaleString()}x
                        </span>
                        {line?.description ?? 'Order line'}
                        {p.picked_up_by ? (
                          <span className="text-xs text-muted ml-2">
                            - by {p.picked_up_by}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted tabular whitespace-nowrap">
                        {new Date(p.picked_up_at).toLocaleDateString()}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {err ? <p className="text-sm text-clay">{err}</p> : null}
    </div>
  );
}

function ShipmentSummary({
  shipment,
  lineLookup,
  orderId,
  shipTo,
}: {
  shipment: FulfillmentOrderFulfillmentShipment;
  lineLookup: Map<string, FulfillmentOrderFulfillmentLine>;
  orderId: string;
  shipTo: ShippingShipmentAddress;
}) {
  const ctx = useActiveCtx();
  const canWrite = useCanWriteOrg();
  const cancelShip = useCancelShipment(ctx);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const cancelled = !!shipment.cancelled_at;
  const canEditContents = !cancelled && !shipment.shipped_at;
  const canCancel = !cancelled && !shipment.shipped_at;

  async function onCancel() {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        'Cancel this shipment? Its line allocations will be freed so you can re-assign them. Refund any purchased label separately.',
      )
    ) {
      return;
    }
    setActionErr(null);
    try {
      await cancelShip.mutateAsync({ shipmentId: shipment.id });
    } catch (e) {
      setActionErr(describeError(e));
    }
  }

  return (
    <div className={`py-3 ${cancelled ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusChip status={shipment.status} />
            <span className="tabular text-xs text-muted">
              {shipment.id.slice(0, 8)}
            </span>
            {shipment.carrier ? (
              <span className="text-xs text-muted uppercase">
                {shipment.carrier}
              </span>
            ) : null}
            {shipment.tracking_number ? (
              <span className="text-xs text-muted tabular truncate">
                {shipment.tracking_number}
              </span>
            ) : null}
          </div>
          {shipment.shipped_at ? (
            <p className="mt-1 text-xs text-muted">
              Shipped {new Date(shipment.shipped_at).toLocaleString()}
            </p>
          ) : cancelled ? (
            <p className="mt-1 text-xs text-muted">Cancelled</p>
          ) : null}
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
          {canEditContents && canWrite ? (
            <Link to={`/fulfillment/${shipment.id}`}>
              <Button size="sm" variant="ghost">
                Edit
              </Button>
            </Link>
          ) : null}
          {!cancelled && shipment.status === 'ready_to_pack' && canWrite ? (
            <Link to={`/fulfillment/pack/${shipment.id}`}>
              <Button size="sm" variant="ghost">Pack</Button>
            </Link>
          ) : null}
          {canCancel && canWrite ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={cancelShip.isPending}
              onClick={onCancel}
            >
              {cancelShip.isPending ? 'Cancelling...' : 'Cancel'}
            </Button>
          ) : null}
          <Link to={`/fulfillment/${shipment.id}`}>
            <Button size="sm" variant="ghost">
              Open
            </Button>
          </Link>
        </div>
      </div>
      {shipment.lines.length > 0 ? (
        <ul className="mt-2 ml-4 flex flex-col text-xs text-ink-2">
          {shipment.lines.map((sl) => {
            const line = lineLookup.get(sl.order_line_id);
            return (
              <li key={sl.order_line_id} className="flex justify-between gap-3">
                <span className="truncate">
                  <span className="tabular text-muted mr-2">
                    {Number(sl.quantity).toLocaleString()}x
                  </span>
                  {line?.description ?? 'Order line'}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2 ml-4 text-xs text-muted italic">
          No line assignment captured (legacy shipment).
        </p>
      )}
      {!cancelled ? (
        <ShipmentFulfillmentSection
          shipment={shipment}
          orderId={orderId}
          shipTo={shipTo}
        />
      ) : null}
      {actionErr ? <p className="mt-2 text-sm text-clay">{actionErr}</p> : null}
    </div>
  );
}
